import React, { useState, useEffect, useCallback, useRef } from "react";
import { Select, MenuItem, Popover } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { MinusIcon, PlusIcon } from "@radix-ui/react-icons";
import Tooltip from "@material-ui/core/Tooltip";
import cn from "classnames";
import useTooltipStyles from "./style";
import ColorPickerPopover from "./ColorPickerPopover";
import BorderWeightIcon from "./Primitives/icons/BorderWeightIcon";
import BorderIcon from "./Primitives/BorderIcon";
import { DropdownChevronIcon } from "./Primitives/icons/DropdownChevronIcon";
import { ToggleEyesIcon } from "./SidebarBottomTool";
import { getBorderLabel, hideSelectionTemporarily } from "../../utils";
import { Title } from "./NewEffectSidebar";
import "./styles.css";
import SideBarSlider from "./SidebarSlider";
import { useDragToAdjust } from "../hooks/useDragToAdjust";

// Cap the per-shape outline color rendering — see NewEffectSidebar for
// rationale. Keep in sync with the threshold there + in StyleMenu.
const BULK_SELECTION_RENDER_THRESHOLD = 30;

interface OutlineSidebarProps {
  editor: any;
  selectedShape: any;
  handleSliderChange: any;
  setSelectedShape: any;
  selectedShapes: any[];
}
const borderOptions = [
  { label: "All", value: "border" },
  { label: "Top", value: "borderTop" },
  { label: "Bottom", value: "borderBottom" },
  { label: "Left", value: "borderLeft" },
  { label: "Right", value: "borderRight" },
];

const outlineStyles = [
  { label: "Solid", value: "solid" },
  { label: "Dashed", value: "dashed" },
];

const strokeAlignOptions = [
  { id: "inside", label: "Inside" },
  { id: "center", label: "Center" },
  { id: "outside", label: "Outside" },
];

// Shapes that support all stroke align options including "outside"
const shapesWithOutsideStroke = [
  "customRect",
  "customEllipse",
  "customTriangle",
  "customOval",
  "customStar",
  "customCloud",
  "customHeart",
];

const getStrokeAlignOptionsForShape = (shapeType: string) => {
  return shapesWithOutsideStroke.includes(shapeType)
    ? strokeAlignOptions
    : strokeAlignOptions.filter((o) => o.id !== "outside");
};

const StrokeWidthDragHandle: React.FC<{
  value: number;
  onChange: (v: number) => void;
}> = ({ value, onChange }) => {
  const valueRef = useRef(value);
  valueRef.current = value;
  const { onMouseDown } = useDragToAdjust(
    () => valueRef.current,
    (n) => onChange(Math.max(0, n)),
    { min: 0, step: 1 }
  );
  return (
    <span
      onMouseDown={onMouseDown}
      style={{ cursor: "ew-resize", display: "inline-flex" }}
      title="Drag to adjust width"
    >
      <BorderWeightIcon />
    </span>
  );
};

const OutlineSidebar = ({
  editor,
  selectedShape,
  handleSliderChange,
  setSelectedShape,
  selectedShapes,
}: OutlineSidebarProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorElByShape, setAnchorElByShape] = useState<Record<string, HTMLElement | null>>({});
  const isShapeWithStroke =
    selectedShape?.type === "customText" ||
    selectedShape?.type === "customCloud" ||
    selectedShape?.type === "customHeart" ||
    selectedShape?.type === "customEllipse" ||
    selectedShape?.type === "customOval" ||
    selectedShape?.type === "customTriangle" ||
    selectedShape?.type === "customStar" ||
    selectedShape?.type === "customSvg" ||
    selectedShape?.type === "customDraw" ||
    selectedShape?.type === "customHighlight" ||
    selectedShape?.type === "customRect";
  const [activeItems, setActiveItems] = useState<string[]>([]);
  const [outlineValue, setOutlineValue] = useState(
    isShapeWithStroke ? selectedShape?.props?.strokeWidth : selectedShape?.props?.borderWidth
  );
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [openStrokeAlignMenu, setOpenStrokeAlignMenu] = useState<string | null>(null);
  const strokeAlignRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const classes = useStyles();
  const classesTooltip = useTooltipStyles();

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleClosePopoverForShape = (shapeId: string) => {
    setAnchorElByShape((prev) => ({ ...prev, [shapeId]: null }));
  };

  const updateShapes = (updateFn: (shape: any) => any) => {
    selectedShapes.forEach((shape) => {
      if (!shape) return;
      const updatedShape = updateFn(shape);
      if (!updatedShape || !updatedShape.props) return;
      editor.updateShape(updatedShape);
      setSelectedShape(updatedShape);

      hideSelectionTemporarily(updatedShape, editor);
    });
  };

  const handleSolidDash = (e: any) => {
    updateShapes((shape) => ({
      ...shape,
      props: {
        ...shape.props,
        borderSolid: e.target.value,
      },
    }));
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
        value = value.replace(/^0+/, "");
      }
      if (!isNaN(Number(value)) || value.endsWith(".")) {
        setOutlineValue(value);
        if (!value.endsWith(".")) {
          const propertyToUpdate = isShapeWithStroke ? "strokeWidth" : "borderWidth";
          updateShapes((shape) => ({
            ...shape,
            props: {
              ...shape.props,
              [propertyToUpdate]: Number(value),
            },
          }));
        }
      }
    },
    [handleSliderChange, isShapeWithStroke, editor, selectedShapes, setSelectedShape]
  );

  const handleAllBorder = (value: string, shapeId: string) => {
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;

    // Only apply to shapes that don't have stroke (customText has stroke, other shapes have border)
    const isTargetShapeWithStroke =
      targetShape.type === "customText" ||
      targetShape.type === "customCloud" ||
      targetShape.type === "customHeart" ||
      targetShape.type === "customEllipse" ||
      targetShape.type === "customOval" ||
      targetShape.type === "customTriangle" ||
      targetShape.type === "customStar" ||
      targetShape.type === "customSvg" ||
      targetShape.type === "customDraw" ||
      targetShape.type === "customHighlight" ||
      targetShape.type === "customRect";

    // If the shape has stroke, don't show border side options
    if (isTargetShapeWithStroke) return;

    setActiveItems((prev) => {
      const currentShapeItems = prev.filter((item) => item.includes(shapeId));
      const shapeItems = currentShapeItems.map((item) => item.replace(`${shapeId}-`, ""));

      if (value === "border") {
        const isAllActive =
          shapeItems.includes("borderTop") &&
          shapeItems.includes("borderBottom") &&
          shapeItems.includes("borderLeft") &&
          shapeItems.includes("borderRight");

        const newShapeItems = isAllActive ? [] : ["borderTop", "borderBottom", "borderLeft", "borderRight"];
        return [
          ...prev.filter((item) => !item.includes(shapeId)),
          ...newShapeItems.map((item) => `${shapeId}-${item}`),
        ];
      } else {
        const updatedItems = shapeItems.includes(value)
          ? shapeItems.filter((item) => item !== value)
          : [...shapeItems, value];
        return [...prev.filter((item) => !item.includes(shapeId)), ...updatedItems.map((item) => `${shapeId}-${item}`)];
      }
    });

    // Update only the specific shape
    const currentProps = targetShape.props;
    const newProps = { ...currentProps };

    if (value === "border") {
      const isAllBordersVisible =
        currentProps.isShowBorderTop &&
        currentProps.isShowBorderBottom &&
        currentProps.isShowBorderLeft &&
        currentProps.isShowBorderRight;

      newProps.isShowBorderTop = !isAllBordersVisible;
      newProps.isShowBorderBottom = !isAllBordersVisible;
      newProps.isShowBorderLeft = !isAllBordersVisible;
      newProps.isShowBorderRight = !isAllBordersVisible;
    } else {
      const isAllBordersVisible =
        currentProps.isShowBorderTop &&
        currentProps.isShowBorderBottom &&
        currentProps.isShowBorderLeft &&
        currentProps.isShowBorderRight;

      if (isAllBordersVisible) {
        // Deactivate all borders except the selected one
        newProps.isShowBorderTop = value === "borderTop";
        newProps.isShowBorderBottom = value === "borderBottom";
        newProps.isShowBorderLeft = value === "borderLeft";
        newProps.isShowBorderRight = value === "borderRight";
      } else {
        const borderKey = `isShow${value.charAt(0).toUpperCase() + value.slice(1)}`;
        newProps[borderKey] = !currentProps[borderKey];
      }
    }

    const updatedShape = {
      ...targetShape,
      props: newProps,
    };

    editor.updateShape(updatedShape);

    if (selectedShape && selectedShape.id === shapeId) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.repeat) return;

      const propertyToUpdate = isShapeWithStroke ? "strokeWidth" : "borderWidth";

      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setOutlineValue((prev: number) => {
          const newValue = prev + 1;
          updateShapes((shape) => ({
            ...shape,
            props: { ...shape.props, [propertyToUpdate]: newValue },
          }));
          return newValue;
        });
      } else if (e.key === "-") {
        e.preventDefault();
        setOutlineValue((prev: number) => {
          const newValue = Math.max(0, prev - 1);
          updateShapes((shape) => ({
            ...shape,
            props: { ...shape.props, [propertyToUpdate]: newValue },
          }));
          return newValue;
        });
      }
    },
    [handleSliderChange, isShapeWithStroke, editor, selectedShapes]
  );

  const handleToggleBorderVisibility = (key: string, shapeId: string) => {
    // Only applies to the specific shape that contains this stroke
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape || !targetShape.props.outlineColorMix || !targetShape.props.outlineColorMix[key]) return;

    const shouldHide = !targetShape.props.outlineColorMix[key].isHidden;
    const updatedColorMix = {
      ...targetShape.props.outlineColorMix,
      [key]: { ...targetShape.props.outlineColorMix[key], isHidden: shouldHide },
    };

    const allHidden = Object.values(updatedColorMix).every((color: any) => color.isHidden);

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        outlineColorMix: updatedColorMix,
        isHiddenBorder: allHidden,
      },
    };

    editor.updateShape(updatedShape);

    // Only update selectedShape if it's the specific shape that was modified
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleInputChangeForShape = (e: React.ChangeEvent<HTMLInputElement>, shapeId: string, key: string) => {
    let value = e.target.value;
    if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
      value = value.replace(/^0+/, "");
    }
    if (!isNaN(Number(value)) || value.endsWith(".")) {
      if (!value.endsWith(".")) {
        const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
        if (!targetShape) return;

        const propertyToUpdate = isShapeWithStroke ? "strokeWidth" : "borderWidth";
        const updatedShape = {
          ...targetShape,
          props: {
            ...targetShape.props,
            [propertyToUpdate]: Number(value),
          },
        };

        editor.updateShape(updatedShape);

        if (selectedShape && selectedShape.id === shapeId) {
          setSelectedShape(updatedShape);
        }

        hideSelectionTemporarily(updatedShape, editor);
      }
    }
  };

  const handleOpenPopoverForShape = (event: React.MouseEvent<HTMLElement>, shapeId: string) => {
    setAnchorElByShape((prev) => ({ ...prev, [shapeId]: event.currentTarget }));
  };

  useEffect(() => {
    setOutlineValue(isShapeWithStroke ? selectedShape?.props?.strokeWidth : selectedShape?.props?.borderWidth);
  }, [selectedShape, isShapeWithStroke]);

  useEffect(() => {
    const inputElement = document.getElementById("outline-input");
    if (inputElement) {
      inputElement.addEventListener("keydown", handleKeyPress);
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("keydown", handleKeyPress);
      }
    };
  }, [handleKeyPress]);

  // Close stroke align dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openStrokeAlignMenu) {
        const ref = strokeAlignRefs.current[openStrokeAlignMenu];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenStrokeAlignMenu(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openStrokeAlignMenu]);

  const handleCreateDefaultColor = () => {
    const newColorKey = Date.now().toString();

    updateShapes((shape) => {
      const updatedProps = {
        ...shape.props,
        isCreatedBorderItem: true,
        isShowBorderTop: true,
        isShowBorderBottom: true,
        isShowBorderLeft: true,
        isShowBorderRight: true,
      };

      updatedProps.outlineColorMix = {
        ...updatedProps.outlineColorMix,
        [newColorKey]: { color: "#000000", isHidden: false },
      };

      if (Object.keys(updatedProps.outlineColorMix).length === 1) {
        // Set to 1 only for the first outlineColorMix
        if (isShapeWithStroke) {
          updatedProps.strokeWidth = 1;
        } else {
          updatedProps.borderWidth = 1;
        }
      }

      return {
        ...shape,
        props: updatedProps,
      };
    });
  };

  const handleRemoveColor = (key: string) => {
    const updatedColorMix = { ...selectedShape.props.outlineColorMix };
    delete updatedColorMix[key];

    const isLastColor = Object.keys(updatedColorMix).length === 0;
    const updatedProps = {
      ...selectedShape.props,
      outlineColorMix: updatedColorMix,
      ...(isLastColor && isShapeWithStroke && { strokeWidth: 0 }),
      ...(isLastColor && !isShapeWithStroke && { borderWidth: 0 }),
    };

    editor.updateShape({
      ...selectedShape,
      props: updatedProps,
    });

    setSelectedShape({
      ...selectedShape,
      props: updatedProps,
    });
  };

  const handleStrokeAlignChange = (value: "inside" | "center" | "outside", shapeId: string) => {
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        strokeAlign: value,
      },
    };

    editor.updateShape(updatedShape);

    if (selectedShape && selectedShape.id === shapeId) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  const hasStrokeContent = selectedShapes.some(
    (shape: any) => shape?.props?.outlineColorMix && Object.keys(shape.props.outlineColorMix).length > 0
  );

  return (
    <div
      className={hasStrokeContent ? "flex flex-col gap-2" : undefined}
      style={{ paddingTop: hasStrokeContent ? 0 : 6, paddingBottom: hasStrokeContent ? 0 : 6 }}
    >
      <div className="flex items-center justify-between px-4 gap-2">
        <Title
          name={"Stroke"}
          onClick={selectedShapes.length === 1 || !hasStrokeContent ? handleCreateDefaultColor : undefined}
        />
        {(selectedShapes.length === 1 || !hasStrokeContent) && (
          <PlusIcon className="h-5 w-5 cursor-pointer" onClick={handleCreateDefaultColor} />
        )}
      </div>
      {selectedShapes.some((shape) => shape?.props?.outlineColorMix) &&
        selectedShapes.length <= BULK_SELECTION_RENDER_THRESHOLD && (
          <div className="flex flex-col gap-2 px-4">
            {selectedShapes.map((shape, shapeIndex) => (
              <div key={shape.id} className="flex flex-col w-full gap-2">
                {shape.props?.outlineColorMix && Object.keys(shape.props.outlineColorMix).length > 0 && (
                  <>
                    {Object.entries(shape.props.outlineColorMix).map(([key, colorObj]) => (
                      <div key={`${shape.id}-${key}`} className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <ColorPickerPopover
                            colorProp={isShapeWithStroke ? `outlineColorMix.${key}` : `outlineColorMix.${key}`}
                            selectedShape={shape}
                            editor={editor}
                            setSelectedShape={setSelectedShape}
                            color={(colorObj as any).color}
                            selectedShapes={[shape]}
                          />
                          <div className="flex items-center gap-1">
                            <ToggleEyesIcon
                              isOpen={!(colorObj as { isHidden: boolean }).isHidden}
                              onClick={() => {
                                handleToggleBorderVisibility(key, shape.id);
                              }}
                            />
                            {selectedShapes.length === 1 && (
                              <MinusIcon className="h-5 w-5 cursor-pointer" onClick={() => handleRemoveColor(key)} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Stroke settings - rendered once after all color entries */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        {shape.type !== "customText" && isShapeWithStroke && (
                          <div className="relative" ref={(el) => (strokeAlignRefs.current[shape.id] = el)}>
                            <button
                              onClick={() => setOpenStrokeAlignMenu(openStrokeAlignMenu === shape.id ? null : shape.id)}
                              className="flex items-center justify-between cursor-pointer bg-white transition-colors"
                              style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                border: "1px solid #E2E2FC",
                                height: "34px",
                                width: "150px",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#E2E2FC";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                              }}
                            >
                              <span
                                style={{
                                  color: "#2E3860",
                                  fontFamily: "Inter",
                                  fontSize: "10px",
                                  fontWeight: 400,
                                  lineHeight: "16px",
                                }}
                              >
                                {(() => {
                                  const optionsForShape = getStrokeAlignOptionsForShape(shape.type);
                                  const currentAlign = shape.props?.strokeAlign || "inside";
                                  const effectiveAlign = optionsForShape.some((o) => o.id === currentAlign)
                                    ? currentAlign
                                    : "inside";
                                  const currentOption = optionsForShape.find((opt) => opt.id === effectiveAlign);
                                  return currentOption ? currentOption.label : "Inside";
                                })()}
                              </span>
                              <div
                                className={cn("transition-transform", openStrokeAlignMenu === shape.id && "rotate-180")}
                                style={{ display: "flex", alignItems: "center" }}
                              >
                                <DropdownChevronIcon style={{ position: "static" }} />
                              </div>
                            </button>

                            {openStrokeAlignMenu === shape.id && (
                              <div
                                className="absolute bg-white shadow-lg z-50"
                                style={{
                                  top: "100%",
                                  left: 0,
                                  marginTop: "2px",
                                  border: "1px solid #E2E2FC",
                                  borderRadius: "4px",
                                  minWidth: "100px",
                                }}
                              >
                                {getStrokeAlignOptionsForShape(shape.type).map((option) => {
                                  const optionsForShape = getStrokeAlignOptionsForShape(shape.type);
                                  const currentAlign = shape.props?.strokeAlign || "inside";
                                  const effectiveAlign = optionsForShape.some((o) => o.id === currentAlign)
                                    ? currentAlign
                                    : "inside";
                                  const isSelected = effectiveAlign === option.id;
                                  return (
                                    <button
                                      key={option.id}
                                      onClick={() => {
                                        handleStrokeAlignChange(option.id as "inside" | "center" | "outside", shape.id);
                                        setOpenStrokeAlignMenu(null);
                                      }}
                                      className="flex items-center gap-2 w-full transition-colors"
                                      style={{
                                        padding: "4px 8px",
                                        backgroundColor: isSelected ? "#E2E2FC" : "transparent",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#E2E2FC";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = isSelected ? "#E2E2FC" : "transparent";
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "#2E3860",
                                          fontFamily: "Inter",
                                          fontSize: "10px",
                                          fontWeight: 400,
                                          lineHeight: "16px",
                                        }}
                                      >
                                        {option.label}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          className="flex items-center"
                          style={{
                            border: "1px solid #E4E4EA",
                            borderRadius: "4px",
                          }}
                        >
                          <Tooltip
                            title={isShapeWithStroke ? "Stroke width" : "Outline width"}
                            arrow
                            classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                          >
                            <div
                              style={{
                                paddingLeft: "8px",
                                paddingRight: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "32px",
                              }}
                              className="hover:bg-[#E2E2FC] transition-colors"
                            >
                              <div className="flex items-center gap-1">
                                <StrokeWidthDragHandle
                                  value={
                                    Number(isShapeWithStroke ? shape.props?.strokeWidth : shape.props?.borderWidth) || 0
                                  }
                                  onChange={(n) =>
                                    handleInputChangeForShape(
                                      { target: { value: String(n) } } as any,
                                      shape.id,
                                      "settings"
                                    )
                                  }
                                />
                                <input
                                  id={`outline-input-${shape.id}`}
                                  style={{ border: "none" }}
                                  className="rounded-[3px] w-[22px] h-5 text-center text-[9px] outline-none no-arrows bg-transparent"
                                  type="text"
                                  value={isShapeWithStroke ? shape.props?.strokeWidth : shape.props?.borderWidth}
                                  onChange={(e) => handleInputChangeForShape(e, shape.id, "settings")}
                                  min="0"
                                  autoComplete="off"
                                />
                              </div>
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                      {!isShapeWithStroke &&
                        shape.type !== "customText" &&
                        shape.type !== "customCloud" &&
                        shape.type !== "customHeart" &&
                        shape.type !== "customEllipse" &&
                        shape.type !== "customOval" &&
                        shape.type !== "customTriangle" &&
                        shape.type !== "customStar" &&
                        shape.type !== "customSvg" &&
                        shape.type !== "customDraw" &&
                        shape.type !== "customHighlight" && (
                          <Tooltip
                            title={getBorderLabel(shape?.props, true) || ""}
                            arrow
                            classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                          >
                            <div
                              onClick={(event) => handleOpenPopoverForShape(event, shape.id)}
                              style={{ border: "2px solid #273754" }}
                              className="min-h-[17px] min-w-[20px] flex hover:shadow-md hover:shadow-slate-500 transition-all items-center justify-center text-[9px] text-[#000000] px-1 cursor-pointer"
                            >
                              <p className="-mb-0.5">{getBorderLabel(shape?.props)}</p>
                            </div>
                          </Tooltip>
                        )}
                    </div>

                    {/* Popover for each individual shape */}
                    {!isShapeWithStroke &&
                      shape.type !== "customText" &&
                      shape.type !== "customCloud" &&
                      shape.type !== "customHeart" &&
                      shape.type !== "customEllipse" &&
                      shape.type !== "customOval" &&
                      shape.type !== "customTriangle" &&
                      shape.type !== "customStar" &&
                      shape.type !== "customSvg" &&
                      shape.type !== "customDraw" &&
                      shape.type !== "customHighlight" && (
                        <>
                          <Popover
                            id={`outline-${shape.id}`}
                            open={Boolean(anchorElByShape[shape.id])}
                            anchorEl={anchorElByShape[shape.id]}
                            onClose={() => handleClosePopoverForShape(shape.id)}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "center",
                            }}
                            transformOrigin={{
                              vertical: "bottom",
                              horizontal: "center",
                            }}
                            classes={{ paper: classes.paper }}
                          >
                            <div className="flex gap-2 p-2 items-end">
                              <div
                                style={{ borderColor: "black" }}
                                className="flex outline-posit rounded-[6px] overflow-hidden"
                              >
                                {borderOptions.map(({ label, value }) => (
                                  <Tooltip
                                    key={value}
                                    title={label}
                                    arrow
                                    classes={{ tooltip: classesTooltip.tooltip }}
                                  >
                                    <div
                                      onClick={() => handleAllBorder(value, shape.id)}
                                      className={`flex flex-col items-center justify-center gap-1 cursor-pointer px-2 pb-0.5 rounded-[4px] transition ${
                                        (value === "border" && activeItems.includes(`${shape.id}-border`)) ||
                                        (value !== "border" && activeItems.includes(`${shape.id}-${value}`))
                                          ? "bg-[#D9D9D980]"
                                          : "hover:bg-[#D9D9D980]"
                                      }`}
                                    >
                                      <div className="hover:shadow-slate-500 transition-all">
                                        <BorderIcon
                                          side={value as "borderTop" | "borderLeft" | "borderRight" | "borderBottom"}
                                        />
                                      </div>
                                    </div>
                                  </Tooltip>
                                ))}
                              </div>
                            </div>
                          </Popover>
                        </>
                      )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default OutlineSidebar;

const useStyles = makeStyles({
  inputRoot: {
    "& .MuiOutlinedInput-root": {
      height: "20px",
      padding: "0",
      display: "flex",
      justifyContent: "center",
      border: "1px solid #E2E2FC",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
      "& fieldset": {
        borderColor: "#E2E2FC",
      },
      "&:hover fieldset": {
        borderColor: "#E2E2FC",
      },
      "& .MuiAutocomplete-input": {
        padding: "0px !important",
      },
    },

    "& .MuiAutocomplete-clearIndicator": {
      display: "none !important",
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      paddingRight: "0 !important",
    },
    "& .MuiSelect-iconOutlined": {
      right: "0px !important",
    },
  },
  listPadding: {
    width: "125px",
    marginLeft: "-10px",
    padding: "0px !important",
  },

  paper: {
    borderRadius: "10px !important",
    padding: "0",
    display: "flex",
    justifyContent: "center",
    border: "1px solid #E2E2FC",
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
    transform: "translateY(-15px) !important",
  },
  menuPaper: {
    borderRadius: "4px !important",
    marginTop: "18px",
    marginLeft: "0px",
    width: "52px",
  },
});
